import CountdownTimer from "@/components/countDown";
import Header from "@/components/header";
import useDiscountStore from "@/store/discount";
import useMessageStore from "@/store/message";
import useRegionMessageStore from "@/store/regionMessage";
import { Button, Skeleton } from "@mantine/core";
import { IconAlertTriangle, IconBuildingStore, IconCheck, IconClock, IconReceipt2, IconRefresh, IconX } from "@tabler/icons-react";
import moment from "moment-jalaali";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useGetTransaction from "./hooks/useGetTransaction";
import classes from "./factor.module.css";
import useGetMerchant from "../profile/hooks/useGetMerchant";
import { useGetImage } from "@/utilities/helper/getImage";

const statusContent = {
  0: { title: "در انتظار تایید", description: "نتیجه پرداخت در حال بررسی است", icon: IconClock, tone: "pending" },
  1: { title: "تراکنش ناموفق", description: "پرداخت انجام نشد؛ دوباره تلاش کنید", icon: IconX, tone: "failed" },
  2: { title: "پرداخت با موفقیت انجام شد", description: "رسید تراکنش شما با موفقیت ثبت شد", icon: IconCheck, tone: "success" },
  3: { title: "مبلغ بازگشت داده شد", description: "مبلغ تراکنش به حساب شما بازگشته است", icon: IconRefresh, tone: "returned" },
} as const;

function Factor() {
  const { id } = useParams();
  const { id: discountId } = useDiscountStore();
  const { id: messageId } = useMessageStore();
  const { id: regionMessageId } = useRegionMessageStore();
  const [startCountdown, setStartCountdown] = useState(false);
  const [isCountdownFinished, setIsCountdownFinished] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useGetTransaction({ id: parseInt(id || "0") });
  const merchant = useGetMerchant();
  const { imageUrl: merchantLogo } = useGetImage({ image: merchant.data?.logo, bucket: "merchant" });
  const backToFactor = Boolean(data?.sourceId != null && [discountId, messageId, regionMessageId].includes(data.sourceId));

  const goToLink = () => {
    if (data?.sourceId === discountId) navigate("/dashboard/discount/discountFactor?return=true");
    if (data?.sourceId === messageId) navigate("/dashboard/messages/factor?return=true");
    if (data?.sourceId === regionMessageId) navigate("/dashboard/areaMessage/areaMessageFactor?return=true");
  };

  useEffect(() => { if (backToFactor) setStartCountdown(true); }, [backToFactor]);
  useEffect(() => {
    if (isCountdownFinished) goToLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCountdownFinished]);

  const currentStatus = statusContent[(data?.status ?? 0) as keyof typeof statusContent] ?? {
    title: "وضعیت نامشخص", description: "برای اطلاع از نتیجه، دوباره صفحه را بررسی کنید", icon: IconAlertTriangle, tone: "pending",
  };
  const StatusIcon = currentStatus.icon;
  const rows = [
    { label: "تاریخ و ساعت", value: data?.creationTime ? moment(data.creationTime).format("HH:mm  ·  jYYYY/jMM/jDD") : "—" },
    { label: "مبلغ پرداختی", value: data?.amount != null ? `${data.amount.toLocaleString("fa-IR")} ریال` : "—", featured: true },
    { label: "کد تراکنش", value: data?.id?.toLocaleString("fa-IR") ?? "—" },
    { label: "کد پیگیری بانکی", value: data?.rrn ?? "—" },
  ];

  return (
    <main className={classes.page} dir="rtl">
      <Header title="جزئیات تراکنش" showBack route="/dashboard" />
      <section className={classes.content}>
        <div className={classes.status} data-tone={currentStatus.tone}>
          <span className={classes.statusGlow} />
          <div className={classes.statusIcon}><StatusIcon size={38} strokeWidth={2.2} /></div>
          {isLoading ? <><Skeleton h={26} w={220} radius="xl" /><Skeleton h={16} w={180} radius="xl" mt={10} /></> : <><h1>{currentStatus.title}</h1><p>{currentStatus.description}</p></>}
        </div>
        <section className={classes.merchant} aria-label="اطلاعات مجموعه">
          <div className={classes.merchantLogo}>
            {merchantLogo ? <img src={merchantLogo} alt="" /> : <IconBuildingStore size={24} strokeWidth={1.6} />}
          </div>
          <div className={classes.merchantText}>
            <span>پرداخت به مجموعه</span>
            {merchant.isLoading ? <Skeleton h={18} w={150} radius="xl" /> : <strong>{merchant.data?.businessInfo?.title || "مجموعه شما"}</strong>}
            {(merchant.data?.publicPhone || merchant.data?.phoneNumber) && <small dir="ltr">{merchant.data.publicPhone || merchant.data.phoneNumber}</small>}
          </div>
          {merchant.data?.businessInfo?.subDomain && <span className={classes.site} dir="ltr">{merchant.data.businessInfo.subDomain}.padix.ir</span>}
        </section>
        <section className={classes.receipt} aria-label="رسید تراکنش">
          <div className={classes.receiptHeader}>
            <div><h2>رسید پرداخت</h2><span>اطلاعات تراکنش</span></div>
            <span className={classes.receiptIcon}><IconReceipt2 size={23} /></span>
          </div>
          <div className={classes.rows}>
            {rows.map((row) => <div className={classes.row} data-featured={row.featured || undefined} key={row.label}>
              <span>{row.label}</span>{isLoading ? <Skeleton h={18} w={120} radius="xl" /> : <strong dir="auto">{row.value}</strong>}
            </div>)}
          </div>
          <div className={classes.receiptFooter}><IconCheck size={15} /><span>این رسید در سوابق تراکنش‌های شما ذخیره شده است</span></div>
        </section>
        {backToFactor ? <Button className={classes.primaryAction} size="lg" fullWidth onClick={goToLink}>
          <span>ادامه فرایند</span><span className={classes.countdown}>انتقال خودکار&nbsp;<CountdownTimer initialSeconds={15} onFinish={() => setIsCountdownFinished(true)} startCountdown={startCountdown} resetCountdown={false} size="small" /></span>
        </Button> : <Button className={classes.primaryAction} size="lg" fullWidth onClick={() => navigate("/dashboard")}>بازگشت به خانه</Button>}
        <p className={classes.help}>در صورت کسر وجه و ناموفق بودن تراکنش، مبلغ طبق روال بانکی به حساب شما بازمی‌گردد.</p>
      </section>
    </main>
  );
}

export default Factor;
